// @flow

import * as React from 'react';

import Emojify from 'app/components/shared/Emojify';
import Icon from 'app/components/shared/Icon';
import Dropdown from 'app/components/shared/Dropdown';

import JobComponent from 'app/components/job/Job';

import ManualStep, { type ManualStepType } from './ManualStep';
import TriggerStep, { type TriggerStepType } from './TriggerStep';

import LegacyDispatcher from 'app/lib/legacyDispatcher';
import jobCommandOneliner from 'app/lib/jobCommandOneliner';

type JobStates = (
  'scheduled' |
  'skipped' |
  'assigned' | 'accepted' |
  'blocked' | 'blocked_failed' |
  'limited' |
  'broken' |
  'pending' |
  'running' |
  'canceled' | 'canceling' |
  'timing_out' | 'timed_out' |
  'unblocked' | 'unblocked_failed' |
  'waiting' | 'waiting_failed' |
  'finished'
);

export type ScriptStepType = {
  type: 'script',
  id: string,
  name: ?string,
  step?: {
    label: ?string
  },
  command: ?string,
  state: JobStates,
  passed: ?boolean,
  parallelGroupIndex?: number,
  parallelGroupTotal?: number,
  stepUuid: string,
  retriedInJobUuid?: string,
  softFailed?: boolean,
  tooltip?: string
};

export type WaiterStepType = {
  type: 'waiter',
  id: string,
  state: JobStates,
  continueOnFailure: boolean,
  step?: {
    label: ?string
  }
};

export type { ManualStepType, TriggerStepType };

export type Job = WaiterStepType | ScriptStepType | ManualStepType | TriggerStepType;

class ParallelJobGroup {
  type: 'parallel_group';
  id: string;
  jobs: Array<ScriptStepType>;
  step: ?{
    label: ?string
  };
  name: ?string;
  command: ?string;
  state: JobStates;
  passed: ?boolean;
  total: ?number;
  finished: number;

  constructor(id: string) {
    this.id = id;
    this.type = "parallel_group";
    this.jobs = [];

    this.name = null;
    this.command = null;
    this.state = "scheduled";
    this.passed = null;
    this.total = null;

    this.finished = 0;
  }

  get softFailed() {
    const hasHardFailedJobs = this.jobs.some((job) => !job.passed && !job.softFailed);
    const hasSoftFailedJobs = this.jobs.some((job) => !job.passed && job.softFailed);

    return !hasHardFailedJobs && hasSoftFailedJobs;
  }

  appendJob(job: ScriptStepType) {
    // Set some of the initial values based on the first job we see
    if (!this.total) {
      this.name = job.name;
      this.step = job.step;
      this.command = job.command;
      this.state = job.state;
      this.passed = job.passed;
      this.total = job.parallelGroupTotal;
    }

    // Keep track of jobs that haven't finished yet
    if (job.state === "finished" ||
      job.state === "waiting_failed" ||
      job.state === "blocked_failed" ||
      job.state === "canceled" ||
      job.state === "timed_out" ||
      job.state === "skipped" ||
      job.state === "broken") {
      this.finished += 1;
    }

    // If any job hasn't passed, then the entire group didn't pass
    if (!job.passed) {
      this.passed = false;
    }

    // Figure out the state of the current group
    if (this.finished === this.total) {
      this.state = "finished";
    } else if (job.state === "running" || (this.finished > 0 && (this.finished !== this.total))) {
      this.state = "running";
    }

    this.jobs.push(job);
  }
}

type Props = {
  build: {
    id: string,
    path: string,
    jobs?: Array<Job>,
    jobsCount: number
  }
};

type State = {
  showHiddenJobs: boolean
};

export default class BuildHeaderPipelineComponent extends React.Component<Props, State> {
  state = {
    showHiddenJobs: false
  };

  render() {
    return (
      <div className="flex">
        <div className="flex flex-wrap flex-auto mt1 mb2 mx2">
          {this.stepNodes()}
        </div>
        <div className="flex-none" style={{ paddingTop: 18 }}>
          {this.renderHiddenJobsButton()}
        </div>
      </div>
    );
  }

  renderHiddenJobsButton() {
    // Figure out how many hidden jobs we have (if any?)
    const hiddenJobsCount = this.hiddenJobsCount();
    if (hiddenJobsCount === 0) {
      return null;
    }

    const action = this.state.showHiddenJobs ? "Hide" : "Show";
    const icon = this.state.showHiddenJobs ? "eye-strikethrough" : "eye";
    const suffix = hiddenJobsCount > 1 ? "jobs" : "job";

    return (
      <button
        className="btn dark-gray hover-black regular mt0 ml0 pt0 pl0 mr2"
        onClick={this.handleToggleBrokenStepsClick}
        aria-label={`${action} ${hiddenJobsCount} hidden ${suffix}`}
        style={{ width: 22, height: 22 }}
      >
        <Icon icon={icon} className="relative" style={{ top: -3 }} />
      </button>
    );
  }

  hiddenJobsCount() {
    return (
      this.props.build.jobs
        ? (
          this.props.build.jobs.filter((job) => (
            (job.type !== 'waiter' && job.state === 'broken') || (job.type === 'script' && job.retriedInJobUuid)
          )).length
        )
        : 0
    );
  }

  stepNodes() {
    if (!this.props.build.jobs || this.props.build.jobs.length === 0) {
      return (
        <p className="text-danger" style={{ margin: '10px 0 5px' }}>
          There are no build steps for this pipeline. To add a step, go to your Pipeline Settings.
        </p>
      );
    }

    const jobs = this.state.showHiddenJobs
      ? this.props.build.jobs
      : this.props.build.jobs.filter((job) => job.state !== 'broken' && !(job.type === 'script' && job.retriedInJobUuid));

    let groupedJobs = [];
    let currentParallelGroup = null;

    for (const job of jobs) {
      // Ah! We've stumbled onto a parallel job, let's try and group it.
      if (job.type === 'script' && job.parallelGroupTotal && job.parallelGroupTotal > 1) {
        // If there's no current group being tracked, create one. If there *is*
        // one, and it's different to the current group, we'll finish off that
        // one and create a new group.
        if (!currentParallelGroup) {
          currentParallelGroup = new ParallelJobGroup(job.stepUuid);
        } else if (currentParallelGroup.id !== job.stepUuid) {
          // Only commit the parallel group if we were able to collect all
          // the jobs for it (we may only be showing a partial list of jobs)
          if (currentParallelGroup.jobs.length === currentParallelGroup.total) {
            groupedJobs.push(currentParallelGroup);
          } else {
            groupedJobs = groupedJobs.concat(currentParallelGroup.jobs);
          }

          currentParallelGroup = new ParallelJobGroup(job.stepUuid);
        }

        currentParallelGroup.appendJob(job);
      } else {
        // If this job doesn't have a `parallelGroupTotal`, and there's a
        // `currentParallelGroup` in the mix, we know we've now progressed
        // passed the group onto a new job.
        if (currentParallelGroup) {
          // Only commit the parallel group if we were able to collect all the
          // jobs for it (we may only be showing a partial list of jobs)
          if (currentParallelGroup.jobs.length === currentParallelGroup.total) {
            groupedJobs.push(currentParallelGroup);
          } else {
            groupedJobs = groupedJobs.concat(currentParallelGroup.jobs);
          }

          // We can stop tracking it now
          currentParallelGroup = null;
        }

        groupedJobs.push(job);
      }
    }

    // If there was a dangling parallel group, add it to the list of jobs to
    // render as well.
    if (currentParallelGroup) {
      // Only commit the parallel group if we were able to collect all the
      // jobs for it (we may only be showing a partial list of jobs)
      if (currentParallelGroup.jobs.length === currentParallelGroup.total) {
        groupedJobs.push(currentParallelGroup);
      } else {
        groupedJobs = groupedJobs.concat(currentParallelGroup.jobs);
      }

      currentParallelGroup = null;
    }

    const renderedJobs: Array<React.Node> = groupedJobs.map((job: Job | ParallelJobGroup): React.Node => this.pipelineStep(job));

    // If the build has hidden jobs (such as on the project show / build index
    // page, we want to add a "..." build step that links to the build so you
    // can see all the steps)
    if (this.props.build.jobs && this.props.build.jobs.length !== this.props.build.jobsCount) {
      renderedJobs.push(
        <a
          key={`${this.props.build.id}-more-jobs`}
          href={this.props.build.path}
          className="build-pipeline-job truncate align-middle"
        >
          ...
        </a>
      );
    }

    return renderedJobs;
  }

  pipelineStep(job: Job | ParallelJobGroup, inParallelGroup: boolean = false) {
    const stepClassName = this.stepClassName(job);

    if (job.type === 'script') {
      if (job.state === 'broken') {
        let jobTooltip = job.tooltip || '';

        if (inParallelGroup) {
          if (jobTooltip.length === 0) {
            jobTooltip = this.jobName(job);
          } else {
            jobTooltip = `${this.jobName(job)} ${jobTooltip}`;
          }
        }

        return (
          <div
            key={job.id}
            aria-label={jobTooltip}
            data-tooltip-length="medium"
            className={stepClassName}
            style={{
              maxWidth: inParallelGroup ? null : '15em'
            }}
          >
            {(
              inParallelGroup
                ? (
                  <React.Fragment>
                    <div style={{ flex: '0 0 16px' }}>
                      <i className={JobComponent.prototype.icon(job)} />
                    </div>
                    <div
                      className="ml1 rounded white semi-bold bg-white small tabular-numerals px1 border"
                      style={{
                        color: this.getLabelBackgroundColor(job),
                        borderColor: 'currentColor',
                        lineHeight: 1.75
                      }}
                    >
                      {job.parallelGroupIndex + 1}
                    </div>
                  </React.Fragment>
                )
                : this.jobNameNode(job)
            )}
          </div>
        );
      }

      const href = `${this.props.build.path}#${job.id}`;

      let retriedIcon;

      if (job.retriedInJobUuid) {
        retriedIcon = (
          <Icon
            icon="retry"
            style={{ height: 12, width: 12, marginRight: 5 }}
            className="relative"
          />
        );
      }

      const shouldShowIcon = !job.passed && (
        job.state === "finished" ||
        job.state === "timed_out" ||
        job.state === "canceled"
      );

      return (
        <a
          key={job.id}
          href={href}
          onClick={this.handleScriptJobClick(job)}
          className={stepClassName}
          title={inParallelGroup ? this.jobName(job) : null}
          style={{
            maxWidth: inParallelGroup ? null : '15em'
          }}
        >
          {shouldShowIcon || inParallelGroup ? (
            <span className="pl1 mr1">
              <i className={JobComponent.prototype.icon(job)} />
            </span>
          ) : null}
          {retriedIcon}
          {(
            inParallelGroup
              ? (
                <div
                  className="ml1 rounded white semi-bold bg-white small tabular-numerals px1 border"
                  style={{
                    color: this.getLabelBackgroundColor(job),
                    borderColor: 'currentColor',
                    lineHeight: 1.75
                  }}
                >
                  {job.parallelGroupIndex + 1}
                </div>
              )
              : this.jobNameNode(job)
          )}
        </a>
      );
    } else if (job.type === 'parallel_group') {
      return this.renderParallelGroup(job);
    } else if (job.type === 'waiter') {
      if (job.continueOnFailure) {
        return (
          <div
            key={job.id}
            className={`${stepClassName} dark-gray relative`}
            title="Wait for all previous steps to finish including failures"
          >
            <i
              className="fa fa-times absolute"
              style={{ fontSize: 12, top: 3, right: 3 }}
            />
            <Icon icon="chevron-right" style={{ height: 15, width: 15 }} />
          </div>
        );
      }

      return (
        <div
          key={job.id}
          className={`${stepClassName} dark-gray`}
          title="Wait for all previous steps to pass"
        >
          <Icon icon="chevron-right" style={{ height: 15, width: 15 }} />
        </div>
      );
    } else if (job.type === 'manual') {
      return (
        <ManualStep
          key={job.id}
          build={this.props.build}
          job={job}
          stepClassName={stepClassName}
        />
      );
    } else if (job.type === 'trigger') {
      return (
        <TriggerStep
          key={job.id}
          build={this.props.build}
          job={job}
          stepClassName={stepClassName}
        />
      );
    }

    return (
      <div key={job.id} className={stepClassName}>
        {job.type}
      </div>
    );
  }

  stepClassName(job: Job | ParallelJobGroup) {
    let state = job.state;

    if (job.state === 'finished' || job.state === 'timed_out' || job.state === 'canceled') {
      if (job.type === 'script' || job.type === 'trigger' || job.type === "parallel_group") {
        if (job.passed) {
          state = 'passed';
        } else if (job.softFailed) {
          state = 'failed-soft';
        } else {
          state = 'failed';
        }
      }
    } else if (job.state === 'timing_out' || job.state === 'canceling') {
      state = 'running';
    }

    return [
      'build-pipeline-job',
      `build-pipeline-job-${job.type.replace(/_/g, '-')}`,
      `build-pipeline-state-${state}`,
      'flex',
      'items-center'
    ].join(' ');
  }

  getLabelBackgroundColor(job: ScriptStepType | ParallelJobGroup) {
    switch (job.state) {
      case "pending":
      case "waiting":
      case "blocked":
      case "unblocked":
      case "limited":
      case "scheduled":
      case "assigned":
      case "accepted":
      case "skipped":
      default:
        return "#afafaf"; // Gray

      case "running":
        return "#9c7c14"; // Yellow-ish

      case "finished":
        if (job.passed) {
          return "#69A770"; // Green
        }

        return "#a94442"; // Red

      case "waiting_failed":
      case "blocked_failed":
      case "unblocked_failed":
      case "canceling":
      case "canceled":
      case "timing_out":
      case "timed_out":
        return "#a94442"; // Red
    }
  }

  getWidthForParallelJobCount(parallelGroupTotal: number) {
    return (60 + (parallelGroupTotal.toString(10).length - 1) * 6);
  }

  idealItemsPerRow(itemCount: number) {
    // If we're less than 6, just use one row
    if (itemCount < 6) {
      return itemCount;
    }

    // If we've got less than 16 items, just use two rows
    if (itemCount < 16) {
      return Math.ceil(itemCount / 2);
    }

    // If we've got less than 32 items, just use four rows
    if (itemCount < 32) {
      return Math.ceil(itemCount / 4);
    }

    // Otherwise, we compute something which looks decent
    if (itemCount < 128) {
      return Math.ceil(itemCount / Math.ceil(itemCount / 10));
    }

    return Math.ceil(itemCount / Math.ceil(itemCount / 20));
  }

  renderParallelGroup(group: ParallelJobGroup) {
    const widthForEachJob = this.getWidthForParallelJobCount(group.total || 0);

    const itemsPerRow = this.idealItemsPerRow(group.total || 0);

    return (
      <Dropdown
        key={group.id}
        width={widthForEachJob * itemsPerRow + 10 + 5 * itemsPerRow - 1}
        className={this.stepClassName(group)}
      >
        <div className="right flex items-center cursor-pointer">
          {(group.state === "finished" || group.state === "timed_out") && !group.passed ? (
            <span className="pl1 mr1 flex-none">
              <i className={JobComponent.prototype.icon(group)} />
            </span>
          ) : null}
          <span
            className="truncate"
            style={{
              maxWidth: "12em"
            }}
          >
            {this.jobNameNode(group)}
          </span>
          <span
            className="ml1 rounded white semi-bold small relative tabular-numerals px1"
            style={{
              backgroundColor: this.getLabelBackgroundColor(group),
              lineHeight: 1.75
            }}
          >
            {group.finished}/{group.total}
          </span>
        </div>

        <div
          className="build-pipeline-job-popup mx1 my0 tabular-numerals"
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(${Math.round(widthForEachJob).toString(10)}px, 1fr))`
          }}
        >
          {group.jobs.map((job) => this.pipelineStep(job, true))}
        </div>
      </Dropdown>
    );
  }

  handleScriptJobClick(job: Job) {
    return (evt: SyntheticMouseEvent<HTMLAnchorElement>) => {
      // return if the user did a middle-click, right-click, or used a modifier
      // key (like ctrl-click, meta-click, shift-click, etc.)
      if ((evt.button !== 0) || evt.altKey || evt.ctrlKey || evt.metaKey || evt.shiftKey) {
        return;
      }

      evt.preventDefault();

      LegacyDispatcher.emit("job:focus", { job });
    };
  }

  jobNameNode(job: ScriptStepType | ParallelJobGroup) {
    let text;

    // Ignore step names for jobs in paralell groups
    if (!job.parallelGroupIndex && job.step && job.step.label) {
      // Strip out anything that looks like a parallel number (since this is
      // the raw step label, that data hasn't been interpolated)
      const label = job.step.label.replace(/#?%[nNt]/, "").trim();

      // We don't want to show an empty step, so if after we've replaced %n,
      // there's nothing left, let's just go with the original label.
      if (label === "") {
        if (job.step) {
          text = job.step.label;
        }
      } else {
        text = label;
      }
    }

    // If we've not gotten a label from the step, let's use the job name if
    // it's present
    if (!text && job.name) {
      text = job.name;
    }

    if (text) {
      return <Emojify text={text} className="truncate" style={{ maxWidth: "12em" }} />;
    }

    return (
      <span className="monospace truncate" style={{ fontSize: '0.9em', maxWidth: "12em" }}>
        {jobCommandOneliner(job.command)}
      </span>
    );
  }

  jobName(job: ScriptStepType | ParallelJobGroup) {
    return job.name || job.command || '';
  }

  handleToggleBrokenStepsClick = (evt: SyntheticMouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    if (evt.target instanceof HTMLElement) {
      evt.target.blur();
    }
    return this.setState({ showHiddenJobs: !this.state.showHiddenJobs });
  }
}
